import { Injectable } from '@angular/core';
import { Store, StateToken, State, Action, StateContext, Selector } from '@ngxs/store';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { LocalStorageService } from '../services/local-storage.service';
import {
  LoadWorkRestUiPreferences,
  SetDailyCrewColoringPreference,
  SetLanguage,
  SetMatrixColoringPreference,
  ToggleDarkMode,
  ToggleToolbar,
} from './ui.actions';

export interface UiStateModel {
  isDarkMode: boolean;
  language: string;
  isToolbarCollapsed: boolean;
  dailyCrewColoringEnabled: boolean;
  matrixColoringEnabled: boolean;
}

interface WorkRestUiPreferences {
  dailyCrewColoringEnabled?: boolean;
  matrixColoringEnabled?: boolean;
}

const UI_STATE_TOKEN = new StateToken<UiStateModel>('ui');
const WR_UI_PREFS_KEY = 'workRestUiPreferences';

@State<UiStateModel>({
  name: UI_STATE_TOKEN,
  defaults: {
    isDarkMode: false,
    language: localStorage.getItem('language') || 'en',
    isToolbarCollapsed: false,
    dailyCrewColoringEnabled: true,
    matrixColoringEnabled: false,
  },
})
@Injectable()
export class UiState {
  constructor(
    private service: LocalStorageService,
    private store: Store
  ) {}

  @Action(ToggleDarkMode)
  ToggleDarkMode(
    { getState, setState }: StateContext<UiStateModel>,
    { isDarkMode }: ToggleDarkMode
  ) {
    const state = getState();
    setState({
      ...state,
      isDarkMode,
    });
    return this.service.setSavedState(isDarkMode ? 'darkMode' : '', 'theme');
  }

  @Action(SetLanguage)
  SetLanguage({ getState, setState }: StateContext<UiStateModel>, { language }: SetLanguage) {
    const state = getState();
    setState({
      ...state,
      language,
    });
    // Store language directly in localStorage without JSON.stringify
    localStorage.setItem('language', language);
  }

  @Action(ToggleToolbar)
  ToggleToolbar(
    { getState, setState }: StateContext<UiStateModel>,
    { isToolbarCollapsed }: ToggleToolbar
  ) {
    const state = getState();
    setState({
      ...state,
      isToolbarCollapsed,
    });
  }

  @Action(LoadWorkRestUiPreferences)
  loadWorkRestUiPreferences({ getState, setState }: StateContext<UiStateModel>) {
    const currentState = getState();
    const savedPreferences = this.getSavedWorkRestUiPreferences();

    setState({
      ...currentState,
      ...this.toWorkRestUiStatePatch(savedPreferences, currentState),
    });
  }

  @Action(SetDailyCrewColoringPreference)
  setDailyCrewColoringPreference(
    stateContext: StateContext<UiStateModel>,
    { enabled }: SetDailyCrewColoringPreference
  ) {
    this.updateWorkRestUiPreferences(stateContext, {
      dailyCrewColoringEnabled: enabled,
    });
  }

  @Action(SetMatrixColoringPreference)
  setMatrixColoringPreference(
    stateContext: StateContext<UiStateModel>,
    { enabled }: SetMatrixColoringPreference
  ) {
    this.updateWorkRestUiPreferences(stateContext, {
      matrixColoringEnabled: enabled,
    });
  }

  @Selector()
  static isDarkMode(state: UiStateModel) {
    return state.isDarkMode;
  }

  @Selector()
  static language(state: UiStateModel) {
    return state.language;
  }

  @Selector()
  static isToolbarCollapsed(state: UiStateModel) {
    return state.isToolbarCollapsed;
  }

  @Selector()
  static getDailyCrewColoringEnabled(state: UiStateModel) {
    return state.dailyCrewColoringEnabled;
  }

  @Selector()
  static getMatrixColoringEnabled(state: UiStateModel) {
    return state.matrixColoringEnabled;
  }

  private getSavedWorkRestUiPreferences(): WorkRestUiPreferences | null {
    const storageKey = this.getWorkRestUiPreferencesStorageKey();
    if (!storageKey) {
      return null;
    }

    try {
      return this.service.getSavedState(storageKey) as WorkRestUiPreferences;
    } catch {
      return null;
    }
  }

  private saveWorkRestUiPreferences(preferences: WorkRestUiPreferences): void {
    const storageKey = this.getWorkRestUiPreferencesStorageKey();
    if (!storageKey) {
      return;
    }

    try {
      this.service.setSavedState(preferences, storageKey);
    } catch {
      return;
    }
  }

  private getWorkRestUiPreferencesStorageKey(): string | null {
    const userId = this.store.selectSnapshot(TenantUserAccessState.userAccess)?.id ?? null;
    return userId ? `${WR_UI_PREFS_KEY}_${userId}` : null;
  }

  private updateWorkRestUiPreferences(
    { getState, setState }: StateContext<UiStateModel>,
    patch: WorkRestUiPreferences
  ): void {
    const nextState = {
      ...getState(),
      ...patch,
    };

    setState(nextState);
    this.saveWorkRestUiPreferences(this.toWorkRestUiPreferences(nextState));
  }

  private toWorkRestUiStatePatch(
    preferences: WorkRestUiPreferences | null,
    fallbackState: Pick<UiStateModel, 'dailyCrewColoringEnabled' | 'matrixColoringEnabled'>
  ): Required<WorkRestUiPreferences> {
    return {
      dailyCrewColoringEnabled:
        preferences?.dailyCrewColoringEnabled ?? fallbackState.dailyCrewColoringEnabled,
      matrixColoringEnabled:
        preferences?.matrixColoringEnabled ?? fallbackState.matrixColoringEnabled,
    };
  }

  private toWorkRestUiPreferences(
    state: Pick<UiStateModel, 'dailyCrewColoringEnabled' | 'matrixColoringEnabled'>
  ): WorkRestUiPreferences {
    return {
      dailyCrewColoringEnabled: state.dailyCrewColoringEnabled,
      matrixColoringEnabled: state.matrixColoringEnabled,
    };
  }
}
